import { useEffect, useState, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { MediaItem } from '@/lib/api-services/media-service';
import { useMediaStore } from '@/store/mediaStore';
import type { DragData } from './DndProvider';

interface DraggableMediaItemProps {
  item: MediaItem;
  onDelete: (item: MediaItem) => void;
}

function DraggableMediaItem({ item, onDelete }: DraggableMediaItemProps) {
  const dragData: DragData = {
    type: 'new-block',
    blockType: 'image',
    imageUrl: item.url,
    imageFileName: item.fileName,
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `media-${item.url}`,
    data: dragData,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`lw-media-item group relative aspect-square rounded-md border border-border bg-card overflow-hidden hover:border-primary transition-colors cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-40' : 'opacity-100'
      }`}
    >
      <img
        src={item.url}
        alt={item.fileName}
        className="object-cover pointer-events-none absolute inset-0 w-full h-full"
      />

      {/* Overlay with actions */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="p-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors"
          title="Delete"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(item.url);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="p-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          title="Copy URL"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </button>
      </div>

      {/* Filename tooltip */}
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-xs text-white truncate" title={item.fileName}>
          {item.fileName}
        </p>
      </div>
    </div>
  );
}

export function MediaPanel() {
  const {
    items: mediaItems,
    isLoading: loading,
    isUploading: uploading,
    error,
    fetchMedia,
    uploadMedia,
    deleteMedia,
  } = useMediaStore();

  const [isDragging, setIsDragging] = useState(false);

  // Fetch on mount — no-op if already cached by the store
  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    await uploadMedia(Array.from(files));
  }, [uploadMedia]);

  const handleDelete = async (item: MediaItem) => {
    if (!confirm(`Delete ${item.fileName}?`)) return;
    await deleteMedia(item);
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    handleFileUpload(files);
  }, [handleFileUpload]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(e.target.files);
  };

  return (
    <div className="lw-media-panel flex h-full flex-col">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 mb-4 transition-colors ${
          isDragging
            ? 'border-primary bg-primary/10'
            : 'border-border hover:border-primary/50'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="media-upload"
          className="hidden"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          onChange={handleFileInputChange}
          disabled={uploading}
        />
        
        <label
          htmlFor="media-upload"
          className="flex flex-col items-center justify-center cursor-pointer"
        >
          <svg
            className="h-10 w-10 text-muted-foreground mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="text-sm text-muted-foreground text-center">
            {uploading ? (
              <span className="text-primary">Uploading...</span>
            ) : (
              <>
                <span className="font-medium">Click to upload</span> or drag and drop
                <br />
                <span className="text-xs">PNG, JPG, GIF up to 10MB</span>
              </>
            )}
          </p>
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 rounded-md bg-destructive/10 border border-destructive text-destructive text-sm">
          {error}
        </div>
      )}

      {/* Media Grid */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-muted-foreground">Loading media...</div>
          </div>
        ) : mediaItems.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-muted-foreground">No media files yet</div>
          </div>
        ) : (
          <div className="lw-media-grid grid grid-cols-2 gap-3">
            {mediaItems.map((item) => (
              <DraggableMediaItem
                key={item.url}
                item={item}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
